import { useCallback, useMemo } from 'react';
import { apiCaller } from 'utils/axios';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import { logger } from 'utils/environment/log';
import { useNotificationsContext } from './context';
import { ACTIONS, NotificationReadStatus } from './reducer';

export const useNotifications = () => {
    const [state, dispatch] = useNotificationsContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            const errorMessage = 'An unexpected error occurred';
            const errors = null;

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    // Fetch notifications with pagination and filter by read status
    const fetchNotifications = useCallback(
        async (notificationRead = NotificationReadStatus.ALL, pagination = { page: 0, size: 20 }) => {
            try {
                setLoading(true);

                const params = {
                    return: notificationRead,
                    page: pagination.page,
                    size: pagination.size
                };

                const response = await apiCaller.get(API_ENDPOINTS.NOTIFICATIONS, { params });

                dispatch({
                    type: ACTIONS.SET_NOTIFICATIONS,
                    payload: response
                });

                return response;
            } catch (error) {
                logger.error('Error fetching notifications:', error);
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [dispatch, handleError, setLoading]
    );

    // Mark a notification as read
    const markAsRead = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const response = await apiCaller.put(`${API_ENDPOINTS.NOTIFICATIONS}/${id}`);

                dispatch({
                    type: ACTIONS.MARK_AS_READ,
                    payload: response
                });

                return response;
            } catch (error) {
                logger.error('Error marking notification as read:', error);
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [dispatch, handleError, setLoading, showSuccessMessage]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    return useMemo(
        () => ({
            // State
            notifications: state.notifications,
            loading: state.loading,
            error: state.error,
            pagination: state.pagination,
            lastUpdated: state.lastUpdated,

            // Methods
            fetchNotifications,
            markAsRead,
            clearError,

            // Constants
            NotificationReadStatus
        }),
        [state, fetchNotifications, markAsRead, clearError]
    );
};
